import AddIcon from "@mui/icons-material/Add";
import {
  Box,
  Chip,
  CircularProgress,
  IconButton,
  Tooltip,
  Typography,
} from "@mui/material";
import type React from "react";
import { useCallback, useEffect, useState } from "react";
import { getUserTags, removeTagFromUser } from "../../apis/tags/tag-apis";
import { getContrastColor, type UserTagWithDetails } from "../../domain/tags";
import { useShowToast } from "../../hooks";
import AddTagModal from "../modal/addTagModal";

interface CustomerTagsProps {
  userId: string;
  adminEmail: string;
  onTagChange?: () => void;
}

const CustomerTags: React.FC<CustomerTagsProps> = ({
  userId,
  adminEmail,
  onTagChange,
}) => {
  const [tags, setTags] = useState<UserTagWithDetails[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [removingTagId, setRemovingTagId] = useState<string | null>(null);
  const [addModalOpen, setAddModalOpen] = useState(false);
  const showToast = useShowToast();

  const fetchTags = useCallback(async () => {
    try {
      setLoading(true);
      setError(false);
      const userTags = await getUserTags(userId);
      setTags(userTags);
    } catch (err) {
      console.error("Error fetching user tags:", err);
      setError(true);
    } finally {
      setLoading(false);
    }
  }, [userId]);

  useEffect(() => {
    if (userId) {
      fetchTags();
    }
  }, [userId, fetchTags]);

  const handleRemoveTag = async (tagId: string) => {
    setRemovingTagId(tagId);
    try {
      await removeTagFromUser(userId, tagId);
      showToast("Tag removed", "success");
      await fetchTags();
      onTagChange?.();
    } catch (error) {
      showToast("Failed to remove tag", "error");
    } finally {
      setRemovingTagId(null);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 2 }}>
        <CircularProgress size={20} />
      </Box>
    );
  }

  return (
    <Box>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 1,
        }}
      >
        <Typography variant="subtitle2" color="textSecondary">
          Tags
        </Typography>
        <IconButton size="small" onClick={() => setAddModalOpen(true)}>
          <AddIcon fontSize="small" />
        </IconButton>
      </Box>

      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
        {error ? (
          <Typography variant="body2" color="textSecondary">
            Unable to load tags
          </Typography>
        ) : tags.length === 0 ? (
          <Typography variant="body2" color="textSecondary">
            No tags
          </Typography>
        ) : (
          tags.map((userTag) => (
            <Tooltip
              key={userTag.id}
              title={
                <Box>
                  <div>Added by {userTag.appliedByEmail}</div>
                  <div>{new Date(userTag.appliedAt).toLocaleDateString()}</div>
                  {userTag.note && <div>Note: {userTag.note}</div>}
                </Box>
              }
            >
              <Chip
                label={userTag.tag.name}
                size="small"
                onDelete={() => handleRemoveTag(userTag.tag.id)}
                disabled={removingTagId === userTag.tag.id}
                deleteIcon={
                  removingTagId === userTag.tag.id ? (
                    <CircularProgress
                      size={14}
                      sx={{ color: getContrastColor(userTag.tag.color) }}
                    />
                  ) : undefined
                }
                sx={{
                  backgroundColor: userTag.tag.color,
                  color: getContrastColor(userTag.tag.color),
                  "& .MuiChip-deleteIcon": {
                    color: getContrastColor(userTag.tag.color),
                  },
                }}
              />
            </Tooltip>
          ))
        )}
      </Box>

      <AddTagModal
        open={addModalOpen}
        onClose={() => setAddModalOpen(false)}
        userId={userId}
        adminEmail={adminEmail}
        existingTagIds={tags.map((t) => t.tag.id)}
        onSuccess={() => {
          fetchTags();
          onTagChange?.();
          setAddModalOpen(false);
        }}
      />
    </Box>
  );
};

export default CustomerTags;
